<script setup lang="ts">
/**
 * Bloc de rendu de tous les custom fields d'un participant adhésion :
 * text, email, phone, number, date, address, select, checkbox, file.
 *
 * Utilisé en Step « Participants » sur la page /a/[slug].
 *
 * ⚠️ Les champs NE SONT PLUS des `<input>` bruts (Karim 2026-09-21) :
 *   - `date`    → calendrier maison (`FieldDatePicker`), l'input natif ayant
 *                 une apparence différente par navigateur ;
 *   - `phone`   → chiffres et séparateurs SEULEMENT, filtrés à la frappe ;
 *   - `address` → autocomplétion Base Adresse Nationale, la même que le
 *                 dashboard et les paramètres d'organisation.
 *
 * ERREURS : affichées SOUS le champ, bordure rouge, et `id` stable
 * (`champ-<id>`) pour que la validation puisse faire défiler jusqu'au premier
 * champ fautif (cf. `allerAuPremierChampEnErreur`).
 */
import { useErreursEffaceesALaSaisie } from "~/composables/useErreursEffaceesALaSaisie";
import type { MembershipCustomField } from "~/types/membership";
import { exempleTelephone } from "@donoor/utils";
import { usePaysAssociation } from "~/composables/usePaysAssociation";
import { BORDURE_CHAMP_PUBLIC, BORDURE_CHAMP_PUBLIC_ERREUR } from "~/utils/stylesChamps";

// Pays de l'association : un téléphone tapé sans indicatif s'y lit.
const paysAsso = usePaysAssociation();

const props = defineProps<{
  fields: MembershipCustomField[];
  values: Record<string, unknown>;
  participantIndex: number;
  isFileUploading: (fieldId: string, idx: number) => boolean;
  isFileUploaded: (fieldId: string, idx: number) => boolean;
  getFileUploadError: (fieldId: string, idx: number) => string | undefined;
  /** Erreurs de validation par id de champ. Vide = aucune erreur. */
  erreurs?: Record<string, string>;
  /**
   * Préfixe des `id` HTML. Distinct PAR PARTICIPANT : sur une adhésion
   * famille, deux participants partagent les mêmes ids de champ, et un `id`
   * dupliqué ferait défiler vers le mauvais bloc.
   */
  prefixeId?: string;
}>();

defineEmits<{
  (e: 'upload-file', file: File, fieldId: string, idx: number): void;
  (e: 'remove-file', fieldId: string, idx: number): void;
}>();

const values = computed(() => props.values);

/** Id HTML stable d'un champ — cible du défilement et du `<label for>`. */
function idChamp(fieldId: string): string {
  return `${props.prefixeId ?? "champ"}-${fieldId}`;
}

/** Réponses retenues d'un choix multiple (une ancienne réponse unique compte). */
function reponsesDe(fieldId: string): string[] {
  const v = values.value[fieldId];
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === "string");
  return typeof v === "string" && v !== "" ? [v] : [];
}

/** Coche / décoche une option, en gardant l'ordre des options. */
function basculerOption(field: MembershipCustomField, option: string) {
  const avant = reponsesDe(field.id);
  const apres = avant.includes(option) ? avant.filter((o) => o !== option) : [...avant, option];
  const ordre = field.options ?? [];
  values.value[field.id] = ordre.filter((o, i) => apres.includes(o) && ordre.indexOf(o) === i);
}

// Une erreur s'efface dès qu'on modifie le champ (cf. composable).
const { erreurDe } = useErreursEffaceesALaSaisie(
  () => props.erreurs,
  () => ({ ...props.values }),
);

/** Classes de l'input selon l'état d'erreur — évite de répéter 8 fois la chaîne. */
function classesInput(fieldId: string): string {
  const base =
    "w-full px-4 py-3 rounded-xl bg-white dark:bg-[#0f0f0f] text-sm text-[#2d2e5c] dark:text-white placeholder-gray-400 transition-colors";
  return `${base} ${erreurDe(fieldId) ? BORDURE_CHAMP_PUBLIC_ERREUR : BORDURE_CHAMP_PUBLIC}`;
}

/**
 * Téléphone : on ne garde que ce qui peut composer un numéro.
 *
 * `type="tel"` n'empêche RIEN — il change seulement le clavier mobile. Sans ce
 * filtre, « Zéro six… » partait tel quel en base (Karim 2026-09-21 : « ça doit
 * accepter uniquement des chiffres »). Espaces, points, tirets, parenthèses et
 * le `+` international restent admis : ils font partie d'un numéro écrit.
 */
function filtrerTelephone(fieldId: string, e: Event) {
  const cible = e.target as HTMLInputElement;
  const propre = cible.value.replace(/[^\d\s+().-]/g, "");
  if (cible.value !== propre) cible.value = propre;
  values.value[fieldId] = propre;
}

// Nom du fichier uploadé pour un champ (l'assertion de type est faite ici, dans
// le script, et non dans le template : vue-tsc ne parse pas les `as` inline).
function uploadedFileName(fieldId: string): string | undefined {
  return (props.values[fieldId] as { file_name?: string } | undefined)?.file_name;
}
</script>

<template>
  <div class="space-y-4">
    <div v-for="field in fields" :key="field.id">
      <label
        :for="idChamp(field.id)"
        class="block text-sm font-medium text-[#2d2e5c] dark:text-gray-300 mb-1.5"
      >
        {{ field.label }}
        <span v-if="field.is_required" class="text-red-500">*</span>
      </label>

      <input
        v-if="field.field_type === 'text'"
        :id="idChamp(field.id)"
        v-model="values[field.id]"
        type="text"
        :placeholder="field.placeholder || ''"
        :aria-invalid="!!erreurDe(field.id) || undefined"
        :aria-describedby="erreurDe(field.id) ? `${idChamp(field.id)}-erreur` : undefined"
        :class="classesInput(field.id)"
      />
      <input
        v-else-if="field.field_type === 'email'"
        :id="idChamp(field.id)"
        v-model="values[field.id]"
        type="email"
        inputmode="email"
        autocomplete="email"
        :placeholder="field.placeholder || 'email@exemple.com'"
        :aria-invalid="!!erreurDe(field.id) || undefined"
        :aria-describedby="erreurDe(field.id) ? `${idChamp(field.id)}-erreur` : undefined"
        :class="classesInput(field.id)"
      />
      <input
        v-else-if="field.field_type === 'phone'"
        :id="idChamp(field.id)"
        :value="values[field.id] ?? ''"
        type="tel"
        inputmode="tel"
        autocomplete="tel"
        :placeholder="field.placeholder || exempleTelephone(paysAsso)"
        :aria-invalid="!!erreurDe(field.id) || undefined"
        :aria-describedby="erreurDe(field.id) ? `${idChamp(field.id)}-erreur` : undefined"
        :class="classesInput(field.id)"
        @input="filtrerTelephone(field.id, $event)"
      />
      <input
        v-else-if="field.field_type === 'number'"
        :id="idChamp(field.id)"
        v-model.number="values[field.id]"
        type="number"
        inputmode="numeric"
        :placeholder="field.placeholder || ''"
        :aria-invalid="!!erreurDe(field.id) || undefined"
        :aria-describedby="erreurDe(field.id) ? `${idChamp(field.id)}-erreur` : undefined"
        :class="classesInput(field.id)"
      />

      <!-- Date : calendrier maison, saisie clavier conservée. -->
      <FieldDatePicker
        v-else-if="field.field_type === 'date'"
        :id="idChamp(field.id)"
        v-model="values[field.id] as string | undefined"
        :en-erreur="!!erreurDe(field.id)"
      />

      <!-- Adresse : autocomplétion Base Adresse Nationale. -->
      <FieldAddressInput
        v-else-if="field.field_type === 'address'"
        :id="idChamp(field.id)"
        v-model="values[field.id] as string | undefined"
        :placeholder="field.placeholder || undefined"
        :en-erreur="!!erreurDe(field.id)"
      />

      <!-- Choix MULTIPLE : cases carrées, plusieurs réponses possibles. -->
      <div
        v-else-if="field.field_type === 'select' && field.allow_multiple && field.options"
        :id="idChamp(field.id)"
        tabindex="-1"
        role="group"
        :aria-label="field.label"
        class="space-y-2 focus:outline-none"
      >
        <p class="text-xs text-gray-500 dark:text-gray-400">{{ $t('adhesion.aslugCustomFieldsBlock.plusieursReponsesPossibles') }}</p>
        <button
          v-for="option in [...new Set(field.options)]"
          :key="option"
          type="button"
          role="checkbox"
          :aria-checked="reponsesDe(field.id).includes(option)"
          class="w-full flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors text-left"
          :class="reponsesDe(field.id).includes(option)
            ? 'border-[#212121] dark:border-white bg-[#f5f5f5] dark:bg-[#1a1a1a]'
            : erreurDe(field.id)
              ? 'border-red-500 dark:border-red-500 hover:border-red-600'
              : 'border-gray-200 dark:border-[#262626] hover:border-gray-300 dark:hover:border-gray-600'"
          @click="basculerOption(field, option)"
        >
          <div
            class="w-4 h-4 rounded-[4px] border-2 shrink-0 flex items-center justify-center transition-colors"
            :class="reponsesDe(field.id).includes(option) ? 'border-[#212121] dark:border-white bg-[#212121] dark:bg-white' : 'border-gray-300 dark:border-gray-600'"
          >
            <UIcon v-if="reponsesDe(field.id).includes(option)" name="i-lucide-check" class="size-3 text-white dark:text-[#212121]" />
          </div>
          <span class="text-sm text-gray-700 dark:text-gray-300">{{ option }}</span>
        </button>
      </div>

      <div
        v-else-if="field.field_type === 'select' && field.options"
        :id="idChamp(field.id)"
        tabindex="-1"
        class="space-y-2 focus:outline-none"
      >
        <button
          v-for="option in field.options"
          :key="option"
          type="button"
          class="w-full flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors text-left"
          :class="values[field.id] === option
            ? 'border-[#212121] dark:border-white bg-[#f5f5f5] dark:bg-[#1a1a1a]'
            : erreurDe(field.id)
              ? 'border-red-500 dark:border-red-500 hover:border-red-600'
              : 'border-gray-200 dark:border-[#262626] hover:border-gray-300 dark:hover:border-gray-600'"
          @click="values[field.id] = values[field.id] === option ? undefined : option"
        >
          <div
            class="w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors"
            :class="values[field.id] === option ? 'border-[#212121] dark:border-white' : 'border-gray-300 dark:border-gray-600'"
          >
            <div v-if="values[field.id] === option" class="w-2 h-2 rounded-full bg-[#212121] dark:bg-white" />
          </div>
          <span class="text-sm text-gray-700 dark:text-gray-300">{{ option }}</span>
        </button>
      </div>

      <label
        v-else-if="field.field_type === 'checkbox'"
        class="flex items-center gap-3 cursor-pointer"
      >
        <input
          :id="idChamp(field.id)"
          v-model="values[field.id]"
          type="checkbox"
          :aria-invalid="!!erreurDe(field.id) || undefined"
          class="rounded text-[#212121] dark:text-white focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-[#212121] dark:focus-visible:ring-white"
          :class="erreurDe(field.id) ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'"
        />
        <span class="text-sm text-gray-700 dark:text-gray-300">{{ field.placeholder || $t('commun.etats.oui') }}</span>
      </label>

      <div v-else-if="field.field_type === 'file'" :id="idChamp(field.id)" tabindex="-1" class="focus:outline-none">
        <div v-if="isFileUploading(field.id, participantIndex)" class="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-[#262626] bg-gray-50 dark:bg-[#1a1a1a] flex items-center gap-3">
          <UIcon name="i-lucide-loader-2" class="size-4 text-gray-400 animate-spin" />
          <span class="text-sm text-gray-500 dark:text-gray-400">{{ $t('adhesion.aslugCustomFieldsBlock.uploadEnCours') }}</span>
        </div>
        <div v-else-if="isFileUploaded(field.id, participantIndex)" class="w-full px-4 py-3 rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 flex items-center justify-between gap-3">
          <div class="flex items-center gap-2 min-w-0">
            <UIcon name="i-lucide-check-circle" class="size-4 text-green-600 dark:text-green-400 shrink-0" />
            <span class="text-sm text-green-800 dark:text-green-300 truncate">{{ uploadedFileName(field.id) }}</span>
          </div>
          <button type="button" class="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 shrink-0 font-medium" @click="$emit('remove-file', field.id, participantIndex)">{{ $t('commun.actions.supprimer') }}</button>
        </div>
        <div v-else>
          <label
            class="w-full px-4 py-3 rounded-xl border bg-white dark:bg-[#0f0f0f] flex items-center gap-3 cursor-pointer transition-colors"
            :class="erreurDe(field.id)
              ? 'border-red-500 dark:border-red-500'
              : 'border-gray-200 dark:border-[#262626] hover:border-gray-300 dark:hover:border-gray-600'"
          >
            <UIcon name="i-lucide-upload" class="size-4 text-gray-400 shrink-0" />
            <span class="text-sm text-gray-500 dark:text-gray-400">{{ $t('adhesion.aslugCustomFieldsBlock.choisirFichier') }}</span>
            <span class="text-xs text-gray-400 dark:text-gray-500 ml-auto">{{ $t('adhesion.aslugCustomFieldsBlock.formatsAcceptes') }}</span>
            <input
              type="file"
              class="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              @change="(e: Event) => { const target = e.target as HTMLInputElement; const file = target.files?.[0]; if (file) $emit('upload-file', file, field.id, participantIndex); target.value = ''; }"
            />
          </label>
        </div>
        <p v-if="getFileUploadError(field.id, participantIndex)" class="text-xs text-red-500 mt-1">{{ getFileUploadError(field.id, participantIndex) }}</p>
      </div>

      <!-- Message d'erreur, sous le champ concerné. -->
      <p
        v-if="erreurDe(field.id)"
        :id="`${idChamp(field.id)}-erreur`"
        class="flex items-start gap-1.5 text-xs text-red-500 mt-1.5"
      >
        <UIcon name="i-lucide-alert-circle" class="size-3.5 shrink-0 mt-px" />
        <span>{{ erreurDe(field.id) }}</span>
      </p>
    </div>
  </div>
</template>
